/**
 * Security (Option C, C2): how the connection is protected on the wire — SQL Server's Encrypt / Trust pair, the SSL
 * box of PostgreSQL, MySQL (with its Trust box once SSL is on) and Oracle, Oracle's wallet folder — and the SSH tunnel
 * (the four network engines). SQLite, Azure Data Explorer and Azure Storage have none of these, so no Security section.
 *
 * PostgreSQL's SSL encrypts but does not verify the server certificate (`rejectUnauthorized: false` in
 * `pgsqlConnectionManager`), and the row says so.
 */
import type { DatabaseEngine } from '../../components/connectionFormSchema';
import { OracleWalletRow } from '../../components/OracleConnectionFields';
import { SshTunnelFields } from '../../components/SshTunnelFields';
import { FormRow } from '../pane/FormRow';
import { FormSection } from '../pane/FormSection';
import { CHECK_ROW_CLASS, CHECKBOX_CLASS, type ConnectionFormApi } from './fieldControls';
import { hasSecuritySection } from './fieldLabels';

type Props = { form: ConnectionFormApi; engine: DatabaseEngine };

function CheckboxField({ form, name, id, label, testId, note, fallback }: {
  form: ConnectionFormApi; name: string; id: string; label: string; testId?: string; note?: string; fallback: boolean;
}) {
  return (
    <form.Field name={name}>
      {(field: any) => (
        <span className="flex items-center gap-2">
          <input
            id={id}
            name={name}
            type="checkbox"
            className={CHECKBOX_CLASS}
            checked={field.state.value ?? fallback}
            onChange={(event) => field.handleChange(event.target.checked)}
            onBlur={field.handleBlur}
            data-testid={testId}
          />
          <label htmlFor={id}>{label}</label>
          {note && <span className="text-xs text-muted-foreground">{note}</span>}
        </span>
      )}
    </form.Field>
  );
}

/**
 * The checkboxes are keyed by field name: an engine switch renders another field at the same position (`ssl` → `encrypt`),
 * and a `form.Field` instance React reused would keep the previous field's value until the next store change — a Browse
 * Azure pick that switches the engine and writes Encrypt in the same commit showed the PostgreSQL SSL box's state (C3.1).
 */
function EncryptionRow({ form, engine }: Props) {
  if (engine === 'mssql') {
    return (
      <FormRow row="encryption" label="Encryption" groupId="encryption-label">
        <div className={CHECK_ROW_CLASS}>
          <CheckboxField key="encrypt" form={form} name="encrypt" id="encrypt" label="Encrypt connection" testId="encrypt-checkbox" fallback />
          <span className="w-4" aria-hidden />
          <CheckboxField key="trustServerCertificate" form={form} name="trustServerCertificate" id="trustServerCertificate" label="Trust server certificate" note="(self-signed)" testId="trust-server-certificate-checkbox" fallback={false} />
        </div>
      </FormRow>
    );
  }
  return (
    <FormRow
      row="ssl"
      label="SSL"
      groupId="ssl-label"
      hint={engine === 'pgsql' ? 'Encrypts the connection. The server certificate is not verified.' : undefined}
    >
      <div className={CHECK_ROW_CLASS}>
        <CheckboxField key="ssl" form={form} name="ssl" id="ssl" label="Use SSL/TLS" fallback={false} />
        {engine === 'mysql' && (
          <form.Subscribe selector={(state: any) => state.values.ssl}>
            {(sslEnabled: boolean) => sslEnabled ? (
              <>
                <span className="w-4" aria-hidden />
                <CheckboxField key="mysql-trust-cert" form={form} name="trustServerCertificate" id="mysql-trust-cert" label="Trust server certificate" note="(self-signed)" fallback={false} />
              </>
            ) : null}
          </form.Subscribe>
        )}
      </div>
    </FormRow>
  );
}

export function SecuritySection({ form, engine }: Props) {
  if (!hasSecuritySection(engine)) return null;
  return (
    <FormSection id="security" title="Security">
      <EncryptionRow form={form} engine={engine} />
      {engine === 'oracle' && <OracleWalletRow form={form} />}
      <SshTunnelFields form={form} />
    </FormSection>
  );
}
